/**
 * Manipuladores de eventos para o módulo Oferta Formulário
 */

import { validateCPF, isAgeValid, convertBirthdateToISO } from '../utils/formValidations.js';
import { maskTelefone, maskCPF, maskCEP } from '../utils/formMasks.js';
import { sendFormData } from '../utils/formApi.js';
import { isMundoZeroKmHost } from '../utils/attributionUtils.js';
import {
    createFieldErrorSetter,
    setFieldError,
    validateOptinCheckboxes,
    focusFirstInvalidField,
    isPhoneValid,
    EMAIL_REGEX,
    EMAIL_REQUIRED_MESSAGE,
    EMAIL_INVALID_MESSAGE,
    PHONE_REQUIRED_MESSAGE,
    PHONE_INVALID_MESSAGE,
} from '../utils/formValidationUi.js';
import { OPTIN_FIELD_ORDER } from '../renderers/sharedCheckboxes.js';

/**
 * Ordem visual dos campos do modelo `padrao` (ver `buildFormHTML`), usada para
 * focar o primeiro campo inválido na tentativa de submit (AC-2). Campos
 * condicionais ausentes no DOM são simplesmente ignorados.
 * @type {readonly string[]}
 */
const PADRAO_FIELD_ORDER = Object.freeze([
    'name',
    'email',
    'phone',
    'cpf',
    'cep',
    'birthdate',
    ...OPTIN_FIELD_ORDER,
]);

function isFullNameValid(nameValue) {
    const normalized = (nameValue || '').trim().replace(/\s+/g, ' ');
    const parts = normalized.split(' ').filter(Boolean);
    if (parts.length < 2) return false;
    return parts[0].length >= 2 && parts[1].length >= 2;
}

/**
 * Valida o formulário do modelo `padrao` inteiro, marcando **todos** os campos
 * inválidos de uma vez (o handler antigo fazia *early return* no primeiro erro,
 * marcando só um campo).
 *
 * Mudança de comportamento intencional (AC-13 / guardrail LGPD da §6): os
 * checkboxes de opt-in passam a ser validados. O `<form>` do `padrao` tem
 * `novalidate`, o que tornava o `required` dos checkboxes inerte — até aqui o
 * modelo submetia lead com **todos** os consentimentos desmarcados.
 *
 * As demais regras preservam exatamente a obrigatoriedade anterior: nome,
 * e-mail e telefone são obrigatórios; CPF, CEP e data de nascimento só são
 * validados (formato/idade) quando preenchidos.
 *
 * @param {ShadowRoot|DocumentFragment} shadow
 * @param {string} cssPrefix
 * @param {{showErrors?: boolean, touched?: Set<string>|null}} [options]
 *   `showErrors` força a exibição de todas as mensagens (tentativa de submit);
 *   `touched` limita a exibição aos campos já interagidos (validação no blur).
 * @returns {boolean} `true` quando não há nenhum campo inválido.
 */
export function validateForm(shadow, cssPrefix, { showErrors = false, touched = null } = {}) {
    const setFieldError = createFieldErrorSetter(shadow, cssPrefix);
    const canShow = (field) => showErrors || Boolean(touched && touched.has(field));
    let isValid = true;

    // Nome (obrigatório)
    const nameValue = shadow.getElementById(`${cssPrefix}-name`)?.value || '';
    if (!isFullNameValid(nameValue)) {
        isValid = false;
        if (canShow('name')) setFieldError('name', 'Preencha o nome completo.');
    } else {
        setFieldError('name', '');
    }

    // E-mail (obrigatório)
    const emailValue = (shadow.getElementById(`${cssPrefix}-email`)?.value || '').trim();
    if (!emailValue) {
        isValid = false;
        if (canShow('email')) setFieldError('email', EMAIL_REQUIRED_MESSAGE);
    } else if (!EMAIL_REGEX.test(emailValue)) {
        isValid = false;
        if (canShow('email')) setFieldError('email', EMAIL_INVALID_MESSAGE);
    } else {
        setFieldError('email', '');
    }

    // Telefone (obrigatório)
    const phoneValue = shadow.getElementById(`${cssPrefix}-phone`)?.value || '';
    if (!phoneValue.replace(/\D/g, '')) {
        isValid = false;
        if (canShow('phone')) setFieldError('phone', PHONE_REQUIRED_MESSAGE);
    } else if (!isPhoneValid(phoneValue)) {
        isValid = false;
        if (canShow('phone')) setFieldError('phone', PHONE_INVALID_MESSAGE);
    } else {
        setFieldError('phone', '');
    }

    // CPF (opcional; valida formato quando preenchido — comportamento preservado)
    const cpfInput = shadow.getElementById(`${cssPrefix}-cpf`);
    if (cpfInput) {
        const cpfValue = cpfInput.value || '';
        if (cpfValue && !validateCPF(cpfValue)) {
            isValid = false;
            if (canShow('cpf')) setFieldError('cpf', 'CPF inválido. Informe um CPF válido.');
        } else {
            setFieldError('cpf', '');
        }
    }

    // CEP (opcional; valida formato quando preenchido)
    const cepInput = shadow.getElementById(`${cssPrefix}-cep`);
    if (cepInput) {
        const cepValue = cepInput.value || '';
        if (cepValue && cepValue.replace(/\D/g, '').length !== 8) {
            isValid = false;
            if (canShow('cep')) setFieldError('cep', 'CEP deve conter 8 dígitos.');
        } else {
            setFieldError('cep', '');
        }
    }

    // Data de nascimento (opcional; valida idade quando preenchida)
    const birthDateInput = shadow.getElementById(`${cssPrefix}-birthdate`);
    if (birthDateInput) {
        const birthDateValue = birthDateInput.value || '';
        if (birthDateValue && !isAgeValid(birthDateValue)) {
            isValid = false;
            if (canShow('birthdate')) {
                setFieldError('birthdate', 'É preciso ter mais de 18 anos para ter acesso a esta oferta.');
            }
        } else {
            setFieldError('birthdate', '');
        }
    }

    // Opt-ins: uma mensagem por checkbox desmarcado (AC-9)
    if (!validateOptinCheckboxes(shadow, cssPrefix, { showErrors, touched })) {
        isValid = false;
    }

    return isValid;
}

/**
 * Configura máscaras de input e dispara form_start
 * @param {string} [ofertaId] - Identificador canônico da oferta (ver
 *   resolveOfferId em ofertaFormularioHelpers.js, usado por getWidgetInstanceId
 *   em OfertaFormularioModule.js — ordem: hash/campaign_identifier → documentId
 *   → config.id → "sem-id"), reportado no form_start pra permitir correlacionar
 *   qual oferta estava sendo preenchida quando o usuário iniciou o formulário.
 */
export function setupInputMasks(shadow, cssPrefix, ofertaId) {
    let formStartFired = false;
    /** Campos (e `name`s de opt-in) já interagidos — gate de exibição de erro. */
    const touched = new Set();

    const nameInput = shadow.getElementById(`${cssPrefix}-name`);
    const emailInput = shadow.getElementById(`${cssPrefix}-email`);
    const phoneInput = shadow.getElementById(`${cssPrefix}-phone`);
    const cpfInput = shadow.getElementById(`${cssPrefix}-cpf`);
    const cepInput = shadow.getElementById(`${cssPrefix}-cep`);
    const birthDateInput = shadow.getElementById(`${cssPrefix}-birthdate`);

    /**
     * Marca um campo como interagido e revalida o formulário inteiro (só os
     * campos tocados exibem mensagem).
     * @param {string} field
     */
    function touchAndValidate(field) {
        touched.add(field);
        validateForm(shadow, cssPrefix, { touched });
    }

    // Dispara form_start ao completar (blur com conteúdo) o primeiro campo digitado;
    // campos atômicos (select/checkbox/radio) disparam no próprio "change".
    function fireFormStart(fieldName) {
        if (formStartFired) return;
        // Só marca como disparado depois de confirmar que o gtag está
        // disponível — senão, um blur antes do GTM carregar perderia o
        // form_start pra sempre nesta sessão (a flag ficava true sem nunca
        // ter enviado o evento).
        if (typeof window.gtag !== 'function') return;
        formStartFired = true;
        // Sem widget_version aqui: "padrao" fica fora do teste A/B (ver
        // getWidgetVersion em OfertaFormularioModule.js, que só reporta A/B
        // pra form-no-cep/form-2-steps) — mesma regra do generate_lead/lead.
        window.gtag('event', 'form_start', {
            form_id: 'form_widget_oferta',
            form_field: fieldName,
            offer_id: ofertaId || 'sem-id',
            widget_type: 'padrao',
        });
    }

    shadow.addEventListener('focusout', (e) => {
        const el = e.target;
        if (formStartFired || !el) return;
        const tag = el.tagName;
        if (tag !== 'INPUT' && tag !== 'TEXTAREA') return;
        const type = (el.type || '').toLowerCase();
        if (type === 'checkbox' || type === 'radio') return;
        if (!el.value || !String(el.value).trim()) return;
        fireFormStart(el.name || el.id);
    });

    shadow.addEventListener('change', (e) => {
        const el = e.target;
        if (formStartFired || !el) return;
        const tag = el.tagName;
        const type = (el.type || '').toLowerCase();
        if (tag === 'SELECT' || type === 'checkbox' || type === 'radio') {
            fireFormStart(el.name || el.id);
        }
    });

    // Campos de texto: o `input` só aplica máscara — nunca marca "touched" nem
    // valida (AC-13: erro não aparece enquanto o campo está em foco sendo
    // digitado). A exibição de erro fica a cargo do `blur` e do submit.
    if (phoneInput) {
        phoneInput.addEventListener('input', (e) => {
            e.target.value = maskTelefone(e.target.value);
        });

        phoneInput.addEventListener('blur', () => touchAndValidate('phone'));
    }

    if (nameInput) {
        nameInput.addEventListener('blur', () => touchAndValidate('name'));
    }

    if (emailInput) {
        emailInput.addEventListener('blur', () => touchAndValidate('email'));
    }

    if (cpfInput) {
        cpfInput.addEventListener('input', (e) => {
            e.target.value = maskCPF(e.target.value);
        });

        // Validação ao sair do campo
        cpfInput.addEventListener('blur', () => touchAndValidate('cpf'));
    }

    if (cepInput) {
        cepInput.addEventListener('input', (e) => {
            e.target.value = maskCEP(e.target.value);
        });

        cepInput.addEventListener('blur', () => touchAndValidate('cep'));
    }

    if (birthDateInput) {
        birthDateInput.addEventListener('change', () => touchAndValidate('birthdate'));
        birthDateInput.addEventListener('blur', () => touchAndValidate('birthdate'));
    }

    // Opt-ins: cada checkbox exibe a própria mensagem depois de interagido (AC-9)
    shadow
        .querySelectorAll(`#${cssPrefix}-checkboxes input[type="checkbox"]`)
        .forEach((checkbox) => {
            checkbox.addEventListener('change', () => touchAndValidate(checkbox.name));
        });
}

/**
 * Configura controle de placeholder para data.
 *
 * A validação de idade saiu daqui: ela vive em {@link validateForm} (única
 * origem de mensagem de erro de campo), acionada pelos listeners de
 * `setupInputMasks` e pela tentativa de submit.
 */
export function setupBirthdateControl(shadow, cssPrefix) {
    const birthdateInput = shadow.getElementById(`${cssPrefix}-birthdate`);
    if (!birthdateInput) return;

    const birthdateLabel = shadow.querySelector(`.${cssPrefix}-birthdate-placeholder`);
    if (!birthdateLabel) return;

    const updateLabel = () => {
        if (birthdateInput.value) {
            birthdateLabel.style.display = 'none';
        } else {
            birthdateLabel.style.display = 'block';
        }
    };

    birthdateInput.addEventListener('change', updateLabel);
    birthdateInput.addEventListener('input', updateLabel);
    birthdateInput.addEventListener('focus', () => birthdateLabel.style.display = 'none');
    birthdateInput.addEventListener('blur', updateLabel);
}

/**
 * Cria handler de submit do formulário
 */
export function createSubmitHandler(module) {
    return async (event) => {
        event.preventDefault();

        const { cssPrefix } = module.config;
        const form = module.shadow.getElementById(`${cssPrefix}-form`);

        // Limpa mensagens anteriores
        module.hideMessage();

        const formData = new FormData(form);
        const data = Object.fromEntries(formData.entries());

        // Marca TODOS os campos inválidos (sem early return) e foca/rola até o
        // primeiro deles na ordem visual (AC-2). Inclui os opt-ins, que até
        // aqui não eram validados de forma alguma.
        if (!validateForm(module.shadow, cssPrefix, { showErrors: true })) {
            focusFirstInvalidField(module.shadow, cssPrefix, PADRAO_FIELD_ORDER);
            return;
        }

        // Converte data de nascimento para ISO 8601
        if (data.birthDate) {
            data.birthDate = convertBirthdateToISO(data.birthDate);
        }

        try {
            // Dispara evento generated_lead via código (conforme documentação)
            if (typeof window.gtag === 'function') {
                window.gtag('event', 'generate_lead', {
                    event_category: 'Lead',
                    event_label: 'Oferta Formulário',
                    position: 'mzk-widget-' + module.config.id,
                    offer_id: module.getWidgetInstanceId()
                });
            }

            // Envia dados para API
            const ofertaId = module.ofertaData?.hash || module.config.id || 'sem-id';
            const result = await sendFormData(data, ofertaId, module.utmParams);

            if (result.ok || result.success) {
                // Dispara evento lead quando recebe 200 do MotordeLeads
                if (typeof window.gtag === 'function') {
                    window.gtag('event', 'lead', {
                        event_category: 'Lead',
                        event_label: 'Motor v2 Success',
                        widget: module.config.titleForm,
                        offer_id: module.getWidgetInstanceId()
                    });
                }

                // Pixel de conversão OpenAI Ads — só no domínio próprio mundozerokm.com.br
                // (SDK já é inicializado pelo portal via RootLayout; sites de parceiros
                // nunca carregam o SDK, então window.oaiq nem existiria lá).
                if (typeof window.oaiq === 'function' && isMundoZeroKmHost()) {
                    window.oaiq('measure', 'lead_created', { type: 'customer_action' });
                }

                module.showMessage('Cadastro enviado com sucesso!', 'success');
                form.reset();

                // Limpa todas as mensagens de erro de campo
                PADRAO_FIELD_ORDER.forEach((field) => {
                    setFieldError(module.shadow, cssPrefix, field, '');
                });
            } else {
                module.showMessage(result.data?.message || 'Erro ao enviar o cadastro.', 'error');
            }
        } catch (error) {
            console.error('Erro ao enviar formulário:', error);
            module.showMessage('Ocorreu um erro, por favor tente novamente.', 'error');
        }
    };
}
